import { isEmpty, isSchoolAdmin, getBooleanFilterDefaults, getDomainValueForCode, buildTopCountPartitionDomainList, appendCustomCompanyFilterOptions, getDirectoryFilterCountsKey, TOP_COMPANY_GROUP_LABEL } from '../../../library/Core/SmartFunctions';
import { State } from '../../../library/Core/SmartTypes';
import { axiosClient } from '../../../library/Service/axiosClient';

export const COMPANY_OPERATIONAL_STATUS_DOMAIN = 'COMPANY_OPERATIONAL_STATUS';
export const COMPANY_EMPLOYEE_COUNT_DOMAIN = 'COMPANY_EMPLOYEE_COUNT';
export const COMPANY_AGE_DOMAIN = 'COMPANY_AGE';
export const COMPANY_ANNUAL_REVENUE_DOMAIN = 'COMPANY_ANNUAL_REVENUE';
export const JOB_FOUNDER_STATUS_DOMAIN = 'JOB_FOUNDER_STATUS';
export const FIRST_GENERATION_CODE = 'FIRST_GENERATION';
export const FAMILY_MANAGED_CODE = 'FAMILY_MANAGED';

export const REVENUE_SORT_HIGH_TO_LOW = 'HIGH_TO_LOW';
export const REVENUE_SORT_LOW_TO_HIGH = 'LOW_TO_HIGH';
export const REVENUE_SORT_OPTIONS = [
    { code: REVENUE_SORT_HIGH_TO_LOW, value: 'high to low' },
    { code: REVENUE_SORT_LOW_TO_HIGH, value: 'low to high' },
];

class EntrepreneurDirectoryService {
    static filterConfig: any = {
        instituteId: {
            id: 'instituteId',
            label: 'Campus',
            domain: 'INSTITUTE_DOMAIN',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: false,
            isVisible: true,
            hasSearch: true,
            tooltip: 'Campus of the entrepreneur alumnus.',
        },
        programId: {
            id: 'programId',
            label: 'Program Name',
            domain: 'ALL_PROGRAM_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: false,
            isVisible: true,
            hasSearch: true,
            tooltip: 'Academic program of the entrepreneur.',
        },
        passOutYear: {
            id: 'passOutYear',
            label: 'Batch Year',
            domain: 'PAST_YEAR_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: false,
            isVisible: true,
            hasSearch: true,
            tooltip: 'Year of graduation of the entrepreneur.',
        },
        industryCode: {
            id: 'industryCode',
            label: 'Industry',
            domain: 'INDUSTRY_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: false,
            isVisible: true,
            hasSearch: true,
            tooltip: 'Industry of the entrepreneur’s current company.',
        },
        jobCompanyId: {
            id: 'jobCompanyId',
            label: 'Company Name',
            domain: 'COMPANY_CODE',
            domainGroup: 'topPartition',
            topPartitionGroupLabel: TOP_COMPANY_GROUP_LABEL,
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: false,
            isVisible: true,
            hasSearch: true,
            tooltip: 'Current company, including names typed in instead of picked from the list.',
        },
        operationalStatus: {
            id: 'operationalStatus',
            label: 'Operational Status',
            domain: 'COMPANY_OPERATIONAL_STATUS',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: false,
            isVisible: true,
            hasSearch: true,
            tooltip: 'Operational status of the current company from Manage Companies.',
        },
        revenueBand: {
            id: 'revenueBand',
            label: 'Revenue',
            domain: 'COMPANY_ANNUAL_REVENUE',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: false,
            isVisible: true,
            hasSearch: true,
            tooltip: 'Company revenue band from the Balance Sheets tab.',
        },
        employeeCount: {
            id: 'employeeCount',
            label: 'Employee Count',
            domain: 'COMPANY_EMPLOYEE_COUNT',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: false,
            isVisible: true,
            hasSearch: true,
            tooltip: 'Employee count band of the current company.',
        },
        companyAge: {
            id: 'companyAge',
            label: 'Company Age',
            domain: 'COMPANY_AGE',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: false,
            isVisible: true,
            hasSearch: true,
            tooltip: 'Age of the company from Establishment Year.',
        },
        residingCountryCode: {
            id: 'residingCountryCode',
            label: 'Current Country',
            domain: 'COUNTRY_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: false,
            isVisible: true,
            hasSearch: true,
            domainGroup: 'topPartition',
            tooltip: 'Current country of the entrepreneur.',
        },
        residingCityCode: {
            id: 'residingCityCode',
            label: 'Current City',
            domain: 'CITY_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: false,
            isVisible: true,
            hasSearch: true,
            parentIdApply: true,
            parentId: 'residingCountryCode',
            domainGroup: 'topPartition',
            tooltip: 'Current city of the entrepreneur. Depends on country.',
        },
        coFoundedWithAlumnus: {
            id: 'coFoundedWithAlumnus',
            label: 'Co-founded with fellow alumnus',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 1,
            booleanCheckedValue: true,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        openToMentoring: {
            id: 'openToMentoring',
            label: 'Open to Mentoring',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 2,
            booleanCheckedValue: true,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isUnicorn: {
            id: 'isUnicorn',
            label: 'Unicorns',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 3,
            booleanCheckedValue: true,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isFirstGeneration: {
            id: 'isFirstGeneration',
            label: '1st Generation',
            domain: JOB_FOUNDER_STATUS_DOMAIN,
            domainCode: FIRST_GENERATION_CODE,
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 4,
            booleanCheckedValue: true,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isFamilyManaged: {
            id: 'isFamilyManaged',
            label: 'Family Managed',
            domain: JOB_FOUNDER_STATUS_DOMAIN,
            domainCode: FAMILY_MANAGED_CODE,
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 5,
            booleanCheckedValue: true,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        topRevenueEntrepreneurs: {
            id: 'topRevenueEntrepreneurs',
            label: 'Top Revenue Entrepreneurs',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 6,
            booleanCheckedValue: true,
            applicableFor: 'BOTH',
            isVisible: false,
        },
    };

    static pageLoader(params: any = {}) {
        return Promise.all([
            EntrepreneurDirectoryService.getFilterDomain(),
            EntrepreneurDirectoryService.getSummaryCounts(),
        ]).then(([filtersRes, summaryRes]) => ({
            formConfig: {},
            data: {
                searchLevel: 'MY_SCHOOL',
                students: [],
                selectedCard: params.selectedCard || '',
                pagination: { page: 1, pageSize: 50, total: 0, hasMore: false },
                summary: summaryRes.data,
                summaryCountWithoutFilter: summaryRes.data,
            },
            internal: { searchFilterDomains: filtersRes.data || {} },
        }));
    }

    static queryFlag(searchParams: URLSearchParams, key: string): boolean {
        const value = searchParams.get(key);
        if (value == null || value === '' || value === '0' || value.toLowerCase() === 'false') return false;
        return true;
    }

    static normalizeRevenueToken(raw: string) {
        return String(raw || '')
            .replace(/\+/g, ' ')
            .replace(/[\u2013\u2014]/g, '–')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
    }

    static resolveRevenueBandCode(raw: string, domain: any): string {
        const decoded = (() => {
            const s = String(raw || '').replace(/\+/g, ' ').trim();
            try {
                return decodeURIComponent(s);
            } catch {
                return s;
            }
        })();
        if (!decoded) return '';
        const items = domain?.get?.(COMPANY_ANNUAL_REVENUE_DOMAIN) || [];
        const needle = EntrepreneurDirectoryService.normalizeRevenueToken(decoded);
        const hit = items.find(
            (d: any) =>
                EntrepreneurDirectoryService.normalizeRevenueToken(d.code) === needle ||
                EntrepreneurDirectoryService.normalizeRevenueToken(d.value) === needle
        );
        return hit ? String(hit.code) : decoded;
    }

    static resolveRevenueBandCodes(values: string[], domain: any): string[] {
        return (values || [])
            .map((v) => EntrepreneurDirectoryService.resolveRevenueBandCode(v, domain))
            .filter(Boolean);
    }

    static filtersFromSearchParams(searchParams: URLSearchParams, domain?: any) {
        const selectedCard = searchParams.get('selectedCard') || '';
        const instituteId = searchParams.get('instituteId');
        const revenueBandRaw = searchParams.getAll('revenueBand').filter(Boolean);
        const revenueBand = domain
            ? EntrepreneurDirectoryService.resolveRevenueBandCodes(revenueBandRaw, domain)
            : revenueBandRaw;
        return {
            ...EntrepreneurDirectoryService.getDefaultSearchCriteria(),
            selectedCard,
            instituteId: instituteId ? [instituteId] : [],
            revenueBand,
            isFirstGeneration: EntrepreneurDirectoryService.queryFlag(searchParams, 'isFirstGeneration') ? true : null,
            isFamilyManaged: EntrepreneurDirectoryService.queryFlag(searchParams, 'isFamilyManaged') ? true : null,
            topRevenueEntrepreneurs: EntrepreneurDirectoryService.queryFlag(searchParams, 'topRevenueEntrepreneurs')
                ? true
                : null,
        };
    }

    static getSchoolIds(): string[] {
        const user = JSON.parse(localStorage.getItem('user') as string);
        return isSchoolAdmin() ? user.institutes || [] : [user.instituteId].filter(Boolean);
    }

    static getFilterDomain() {
        if (isSchoolAdmin()) {
            return axiosClient().get(
                `${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/entrepreneur-directory/management-filters`
            );
        }
        const schoolId = EntrepreneurDirectoryService.getSchoolIds();
        const qs = schoolId.length ? `?schoolId=${encodeURIComponent(schoolId.join(','))}` : '';
        return axiosClient().get(
            `${process.env.REACT_APP_REPORTS_SERVER}/entrepreneur-directory/search-filter-domains${qs}`
        );
    }

    static getSummaryCounts(criteria: any = {}) {
        const { selectedCard, name, context, revenueSort, ...rest } = EntrepreneurDirectoryService.cleanCriteria({
            schoolId: EntrepreneurDirectoryService.getSchoolIds(),
            level: 'MY_SCHOOL',
            ...criteria,
        });
        return axiosClient().post(
            `${process.env.REACT_APP_REPORTS_SERVER}/entrepreneur-directory/summary-counts`,
            rest
        );
    }

    static async getSummaryCountsBasedOnCampusFilter(searchFilters: any) {
        if (isEmpty(searchFilters?.instituteId)) return null;
        return EntrepreneurDirectoryService.getSummaryCounts({
            schoolId: searchFilters.instituteId,
        });
    }

    static cleanCriteria(criteria: any): any {
        const cleaned: any = {};
        for (const [key, val] of Object.entries(criteria)) {
            if (val === false || val === null || val === undefined) continue;
            if (key === 'revenueSort' && !isSchoolAdmin()) continue;
            cleaned[key] = val;
        }
        return cleaned;
    }

    static getDefaultSearchCriteria() {
        return {
            name: '',
            instituteId: [],
            programId: [],
            passOutYear: [],
            industryCode: [],
            jobCompanyId: [],
            operationalStatus: [],
            revenueBand: [],
            revenueSort: REVENUE_SORT_HIGH_TO_LOW,
            employeeCount: [],
            companyAge: [],
            founderStatus: [],
            residingCountryCode: [],
            residingCityCode: [],
            selectedCard: '',
            ...getBooleanFilterDefaults(EntrepreneurDirectoryService.filterConfig),
            context: { level: 'MY_SCHOOL', page: 1, pageSize: 50 },
        };
    }

    static founderStatusLabel(code: string, state: State, fallback = '') {
        if (!code) return fallback;
        const fromDomain = (state?.domain?.get(JOB_FOUNDER_STATUS_DOMAIN) || []).find(
            (d: any) => String(d.code) === String(code)
        )?.value;
        if (fromDomain) return fromDomain;
        const fromSearch = ((state as any)?.data?.searchDomain?.founderStatus || []).find(
            (d: any) => String(d.code) === String(code)
        )?.value;
        if (fromSearch) return fromSearch;
        const resolved = getDomainValueForCode(code, JOB_FOUNDER_STATUS_DOMAIN, state);
        if (resolved && resolved !== code) return resolved;
        const cfg = Object.values(EntrepreneurDirectoryService.filterConfig).find(
            (c: any) => c.domainCode === String(code)
        ) as any;
        return fallback || cfg?.label || '';
    }

    static withDomainShowProfileLabels(state: State) {
        const config = { ...EntrepreneurDirectoryService.filterConfig };
        return Object.fromEntries(
            Object.entries(config).map(([key, item]: [string, any]) => {
                if (item.domain && item.domainCode) {
                    return [
                        key,
                        {
                            ...item,
                            label: EntrepreneurDirectoryService.founderStatusLabel(item.domainCode, state, item.label),
                        },
                    ];
                }
                return [key, item];
            })
        );
    }

    static getCriteriaForSelectedCard(selectedCard: string, _state: State) {
        const schoolIds = EntrepreneurDirectoryService.getSchoolIds();
        const lookup: any = {
            claimedEntrepreneurs: { schoolId: schoolIds, selectedCard: 'claimedEntrepreneurs' },
            confirmedEntrepreneurs: { schoolId: schoolIds, selectedCard: 'confirmedEntrepreneurs' },
            uniqueCompanies: { schoolId: schoolIds, selectedCard: 'uniqueCompanies' },
            firstGenerationFounders: { schoolId: schoolIds, selectedCard: 'firstGenerationFounders' },
            withFinancialData: { schoolId: schoolIds, selectedCard: 'withFinancialData' },
        };
        return lookup[selectedCard] || { schoolId: schoolIds };
    }

    static async performSearchWithUserSelectedCriteria(
        searchCriteria: any,
        state: State,
        dispatch: any,
        newState: any = {}
    ) {
        const criteria = EntrepreneurDirectoryService.cleanCriteria({
            ...EntrepreneurDirectoryService.getDefaultSearchCriteria(),
            ...searchCriteria,
            schoolId: EntrepreneurDirectoryService.getSchoolIds(),
            context: {
                ...EntrepreneurDirectoryService.getDefaultSearchCriteria().context,
                ...searchCriteria?.context,
                level: 'MY_SCHOOL',
                page: 1,
                pageSize: 50,
            },
        });

        const [searchRes, summaryRes] = await Promise.all([
            axiosClient().post(
                `${process.env.REACT_APP_REPORTS_SERVER}/entrepreneur-directory/search`,
                criteria
            ),
            EntrepreneurDirectoryService.getSummaryCountsBasedOnCampusFilter(searchCriteria).catch(() => null),
        ]);

        const pagination = searchRes.data.pagination || {
            page: 1,
            pageSize: 50,
            total: searchRes.data.totalCount ?? 0,
            hasMore: (searchRes.data.students || []).length === 50,
        };

        dispatch({
            type: 'REFRESH_DATA',
            payload: {
                data: {
                    ...(state as any).data,
                    ...newState,
                    students: searchRes.data.students || [],
                    totalCount: pagination.total ?? searchRes.data.totalCount ?? 0,
                    pagination,
                    filters: searchRes.data.filters || criteria,
                    summary: summaryRes?.data || (state as any).data?.summaryCountWithoutFilter || (state as any).data?.summary,
                    selectedCard: searchCriteria?.selectedCard ?? searchCriteria?.context?.selectedCard ?? (state as any).data.selectedCard,
                },
            },
        });
    }

    static async fetchNextPage(state: State, dispatch: any) {
        const pagination = (state.data as any).pagination;
        if (!pagination?.hasMore) return;

        const nextPage = (pagination.page ?? 1) + 1;
        const criteria = {
            ...(state.data as any).filters,
            schoolId: EntrepreneurDirectoryService.getSchoolIds(),
            context: {
                ...(state.data as any).filters?.context,
                level: 'MY_SCHOOL',
                page: nextPage,
                pageSize: pagination.pageSize || 50,
            },
        };

        const searchRes = await axiosClient().post(
            `${process.env.REACT_APP_REPORTS_SERVER}/entrepreneur-directory/search`,
            criteria
        );

        const newStudents = searchRes.data.students ?? [];
        const existingTotal = (state.data as any).pagination?.total ?? (state.data as any).totalCount ?? null;
        const newPagination = {
            ...(searchRes.data.pagination ?? {}),
            total: searchRes.data.pagination?.total ?? existingTotal,
        };

        dispatch({
            type: 'REFRESH_DATA',
            payload: {
                data: {
                    ...(state as any).data,
                    students: newStudents.length > 0
                        ? [...((state.data as any).students || []), ...newStudents]
                        : (state.data as any).students || [],
                    totalCount: newPagination.total ?? existingTotal ?? 0,
                    pagination: newStudents.length > 0
                        ? newPagination
                        : { ...newPagination, hasMore: false },
                    filters: searchRes.data.filters || criteria,
                },
            },
        });
    }

    static companyAgeBandFromYear(yearValue: unknown): string | null {
        const year = Number(yearValue);
        const currentYear = new Date().getFullYear();
        if (!Number.isInteger(year) || year < 1900 || year > currentYear) return null;
        const age = currentYear - year;
        if (age < 2) return '<2 years';
        if (age <= 5) return '2-5 Years';
        if (age <= 10) return '6-10 years';
        return '10+ years';
    }

    static getSearchFilterDomains(domain: any, customDomains: any) {
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { passOutYear } = user;

        const presentCodes = (codes: any) =>
            (Array.isArray(codes) ? codes : [])
                .map((c: any) => (c != null && typeof c === 'object' ? c.code ?? c.value : c))
                .filter((c: any) => c !== null && c !== undefined && c !== '')
                .map((c: any) => String(c));

        const getDomainForCodes = (domainMap: any, categoryCode: string, codes: any) => {
            if (isEmpty(codes) || !Array.isArray(codes)) return [];
            const stringCodes = presentCodes(codes);
            if (!stringCodes.length) return [];

            let targetDomain = (domainMap?.get(categoryCode) || []).filter((d: any) =>
                stringCodes.includes(String(d.code))
            );
            if (!targetDomain.length) return [];

            if (categoryCode === 'PAST_YEAR_CODE') {
                targetDomain = targetDomain.map((d: any) =>
                    String(d.code) === String(passOutYear) ? { ...d, value: `${d.value} (your batch)` } : d
                );
                return targetDomain.sort((a: any, b: any) =>
                    String(b.value).toLowerCase().localeCompare(String(a.value).toLowerCase())
                );
            }
            if (categoryCode === 'COUNTRY_CODE' || categoryCode === 'CITY_CODE') return targetDomain;
            return targetDomain.sort((a: any, b: any) =>
                String(a.value).toLowerCase().localeCompare(String(b.value).toLowerCase())
            );
        };

        const META_KEYS = new Set([
            'foundingYear',
            'totalRecords',
            'companyAgeYears',
            'residingCountryCodeCounts',
            'residingCityCodeCounts',
        ]);

        const searchFilterDomains: any = {};
        if (customDomains) {
            const payload = { ...customDomains };
            if (!payload.employeeCount) {
                payload.employeeCount = payload.companySize || payload.company_size;
            }
            if (!payload.companyAge) {
                payload.companyAge = [
                    ...new Set(
                        presentCodes(payload.companyAgeYears)
                            .map((year: string) => EntrepreneurDirectoryService.companyAgeBandFromYear(year))
                            .filter(Boolean)
                    ),
                ];
            }

            Object.keys(payload).forEach((key: any) => {
                if (META_KEYS.has(key)) return;
                const cfg = EntrepreneurDirectoryService.filterConfig[key];
                if (!cfg?.domain) return;

                let codes = payload[key];
                if (key === 'revenueBand') {
                    codes = EntrepreneurDirectoryService.resolveRevenueBandCodes(presentCodes(codes), domain);
                }

                let items = getDomainForCodes(domain, cfg.domain, codes);
                if (key === 'jobCompanyId') items = appendCustomCompanyFilterOptions(items, codes);
                if (cfg.domainGroup === 'topPartition') {
                    const counts = payload[getDirectoryFilterCountsKey(key)];
                    searchFilterDomains[key] =
                        counts && Object.keys(counts).length
                            ? buildTopCountPartitionDomainList(items, codes, 10, counts)
                            : items;
                    return;
                }
                searchFilterDomains[key] = items;
            });

            searchFilterDomains.foundingYear = payload.foundingYear || 1950;
        }

        return searchFilterDomains;
    }
}

export default EntrepreneurDirectoryService;
